import _get from 'lodash/get'
import React, { useEffect, useMemo, useRef } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { View } from 'react-native'
import type { ReadonlyDeep } from 'type-fest'

import { useQuoteData } from '@flows-ui/services/QuoteDataContext'
import type { AnyStepContentElement } from '@flows/core/types/stepContentTypes'
import type { ProductKey } from '@flows/index'

type Props = {
  configurableParameters?: ReadonlyDeep<NonNullable<AnyStepContentElement>[]>
  stepContentRenderer: (props: any) => React.ReactElement
  componentMap?: Record<string, React.ElementType>
  /**
   * When set, any change under `products.{key}.configuration_parameters` refetches price
   * (deductible, extensions, etc.). Matches website-react watching product configuration.
   */
  priceWatchProductKey?: ProductKey
}

export const ConfigurableParameters = (props: Props) => {
  const { watch, control } = useFormContext()
  const { flowName, trackPriceChange } = useQuoteData()
  const values = watch()

  const configurationParametersPath = props.priceWatchProductKey
    ? (`products.${props.priceWatchProductKey}.configuration_parameters` as const)
    : undefined

  const formValues = useWatch({ control })
  const watchedConfiguration = useMemo(
    () => (configurationParametersPath ? _get(formValues, configurationParametersPath) : undefined),
    [configurationParametersPath, formValues]
  )

  const trackPriceChangeRef = useRef(trackPriceChange)
  trackPriceChangeRef.current = trackPriceChange

  const watchedSnapshot = useMemo(
    () => JSON.stringify(watchedConfiguration ?? null),
    [watchedConfiguration]
  )

  /** Skip first run after mount so hydration / SSR price is not immediately overwritten. */
  const prevWatchedSnapshot = useRef<string | null>(null)

  useEffect(() => {
    if (!configurationParametersPath) {
      return
    }
    if (prevWatchedSnapshot.current === null) {
      prevWatchedSnapshot.current = watchedSnapshot
      return
    }
    if (watchedSnapshot === prevWatchedSnapshot.current) {
      return
    }

    const handle = setTimeout(() => {
      prevWatchedSnapshot.current = watchedSnapshot
      trackPriceChangeRef.current('configuration_parameter_change')
    }, 350)
    return () => clearTimeout(handle)
  }, [configurationParametersPath, watchedSnapshot])

  if (!props.configurableParameters) {
    return null
  }

  const alignedConfigurableParameters = props.configurableParameters.map((element: any) => {
    if (element?.type === 'harmonyTitle') {
      return { ...element, align: 'left' }
    }
    return element
  })

  return (
    <View className="relative z-10 pt-12">
      {props.stepContentRenderer({
        elements: alignedConfigurableParameters,
        componentMap: props.componentMap,
        flowValues: values,
        flowName,
        hasMinHeight: false,
      })}
    </View>
  )
}
